import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { apiClient } from "@/lib/api/client";
import { toast } from "sonner";
import type {
  AdminTemplate,
  AdminTemplateDetail,
  AdminGuild,
  GuildTemplatePreview,
} from "@/lib/api/types";

export const adminTemplateKeys = {
  all: ["admin", "templates"] as const,
  list: (params?: { offset?: number; limit?: number }) =>
    [...adminTemplateKeys.all, "list", params] as const,
  detail: (id: string) => [...adminTemplateKeys.all, "detail", id] as const,
  guilds: (params?: { search?: string; offset?: number; limit?: number }) =>
    ["admin", "guilds", params] as const,
  guildPreview: (applicationGuildId: string) =>
    ["admin", "guilds", applicationGuildId, "template-preview"] as const,
};

export const adminTemplatesQueryOptions = (params: {
  offset?: number;
  limit?: number;
}) =>
  queryOptions({
    queryKey: adminTemplateKeys.list(params),
    queryFn: () => {
      const searchParams = new URLSearchParams();
      if (params.offset != null)
        searchParams.set("offset", String(params.offset));
      if (params.limit != null) searchParams.set("limit", String(params.limit));
      return apiClient
        .get<{
          data: AdminTemplate[];
          total: number;
        }>(`/api/admin/templates?${searchParams.toString()}`)
        .then((r) => r.data);
    },
    placeholderData: keepPreviousData,
  });

export const adminTemplateDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: adminTemplateKeys.detail(id),
    queryFn: () =>
      apiClient
        .get<{ data: AdminTemplateDetail }>(`/api/admin/templates/${id}`)
        .then((r) => r.data.data),
    enabled: !!id,
  });

export const adminGuildsQueryOptions = (params: {
  search?: string;
  offset?: number;
  limit?: number;
}) =>
  queryOptions({
    queryKey: adminTemplateKeys.guilds(params),
    queryFn: () => {
      const searchParams = new URLSearchParams();
      if (params.search) searchParams.set("search", params.search);
      if (params.offset != null)
        searchParams.set("offset", String(params.offset));
      if (params.limit != null) searchParams.set("limit", String(params.limit));
      return apiClient
        .get<{
          data: AdminGuild[];
          total: number;
        }>(`/api/admin/guilds?${searchParams.toString()}`)
        .then((r) => r.data);
    },
    placeholderData: keepPreviousData,
  });

export const adminGuildPreviewQueryOptions = (applicationGuildId: string) =>
  queryOptions({
    queryKey: adminTemplateKeys.guildPreview(applicationGuildId),
    queryFn: () =>
      apiClient
        .get<{
          data: GuildTemplatePreview;
        }>(`/api/admin/guilds/${applicationGuildId}/template-preview`)
        .then((r) => r.data.data),
    enabled: !!applicationGuildId,
  });

export function useAdminCreateTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: {
      applicationGuildId: string;
      name: string;
      description?: string;
      selections: {
        ticketSettings: boolean;
        categoryIds: number[];
        documentIds: number[];
        threadQAConfigIds: number[];
      };
    }) =>
      apiClient
        .post(`/api/admin/templates/from-guild/${body.applicationGuildId}`, {
          name: body.name,
          description: body.description,
          selections: body.selections,
        })
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: adminTemplateKeys.all,
      });
      toast.success("Template created");
    },
    onError: () => {
      toast.error("Failed to create template");
    },
  });
}

export function useAdminUpdateTemplate(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: {
      name?: string;
      description?: string;
      isPublished?: boolean;
    }) =>
      apiClient.patch(`/api/admin/templates/${id}`, body).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: adminTemplateKeys.all,
      });
    },
  });
}

export function useAdminDeleteTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      apiClient.delete(`/api/admin/templates/${id}`).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: adminTemplateKeys.all,
      });
      toast.success("Template deleted");
    },
    onError: () => {
      toast.error("Failed to delete template");
    },
  });
}
